import { useTypedSelector } from '@Features/store';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import React from 'react';
import styled from 'styled-components';
import useAdminActionPermissions from '../../../StandaloneCourses/useAdminActionPermissions';

/**
 * Shared by the admin and instructor portals (the instructor app re-exports
 * these pages), so imports that must resolve to THIS file's siblings are
 * relative; `@…` aliases resolve to whichever app is building.
 */

export type SubscriptionStatus = 'active' | 'banned' | 'expired' | 'not_enrolled';

export const apiData = <T,>(response: any): T => response.data.data as T;
export const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message || error?.data?.message || fallback;

/**
 * Action gating for the student Subscriptions tab, mirroring the backend:
 * admin portal → role permission codes (super bypasses); instructor portal →
 * assistant grants (main instructors bypass, comma = OR, like instructorCan).
 * The server stays authoritative — this only keeps dead buttons out of view.
 */
export const useSubscriptionGrants = () => {
	const auth: any = useTypedSelector((state) => state.auth);
	const { isAdminPortal } = useAdminActionPermissions();
	const has = (adminCode: string, instructorGrants: string) => {
		if (isAdminPortal)
			return auth.user?.type === 'super' || Boolean(auth.permissions?.has(adminCode));
		if (!auth.user?.parent_id) return true;
		return instructorGrants
			.split(',')
			.some((grant) => Boolean(auth.grants?.has(grant.trim())));
	};
	return {
		isAdminPortal,
		/** enroll + ban/unban (standalone and bundle rosters' routes) */
		canManage: has('u-std-mng-cr', 'classroom_tab'),
		/** unenroll + refund: admin portal only (no instructor route) */
		canUnenroll: isAdminPortal && has('u-std-mng-cr', ''),
		/** reset / sync / complete / unit toggle on standalone lectures */
		canProgress: has('u-std-mng-cr', 'students,course_content'),
		/** per-lecture expiry (lectures/enrollment/update) */
		canExtend: has('u-std-mng-cr', 'classroom_tab'),
		/** Performance report CSVs (same code as the classroom report's) */
		canExport: has('exp-st', 'students'),
	};
};

/**
 * Saves a CSV response (`export: true` endpoints answer with the file body
 * and a Content-disposition filename). Same recipe as the base slices: a Blob
 * with the UTF-8 BOM re-added so Excel keeps Arabic intact.
 */
export const downloadCsv = (response: any, fallbackName = 'export.csv') => {
	const disposition: string | undefined = response.headers?.['content-disposition'];
	const name = disposition?.split('=')[1]?.trim() || fallbackName;
	const blob = new Blob(['﻿' + response.data], { type: 'text/csv;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.setAttribute('href', url);
	anchor.setAttribute('download', name);
	document.body.appendChild(anchor); // required for firefox
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};

const STATUS_VIEW: Record<SubscriptionStatus, { label: string; color: string; Icon: React.ElementType }> = {
	active: { label: 'Active', color: '#00d66b', Icon: CheckCircleIcon },
	banned: { label: 'Banned', color: '#f74343', Icon: BlockIcon },
	expired: { label: 'Expired', color: '#a4a4a4', Icon: RemoveCircleOutlineIcon },
	not_enrolled: { label: 'Not enrolled', color: '#bdbdbd', Icon: RadioButtonUncheckedIcon },
};

/** Subscription state as a single icon; the label shows on hover. */
export const StatusChip: React.FC<{ status: SubscriptionStatus }> = ({ status }) => {
	const view = STATUS_VIEW[status] ?? STATUS_VIEW.not_enrolled;
	return (
		<Tooltip title={view.label} placement="top">
			<view.Icon aria-label={view.label} sx={{ color: view.color, fontSize: 24, verticalAlign: 'middle' }} />
		</Tooltip>
	);
};

/**
 * The ban toggle's icon, the same everywhere (classrooms, standalone courses,
 * bundles): ⊘ red while banned, grey while not.
 */
export const BanIcon: React.FC<{ banned: boolean; disabled?: boolean; size?: number }> = ({ banned, disabled, size = 26 }) => (
	<BlockIcon sx={{ color: disabled ? '#e0e0e0' : banned ? '#f74343' : '#bdbdbd', height: size, width: size }} />
);

const StyledBanButton = styled(IconButton)`
	&.MuiIconButton-root:hover {
		background: rgba(247, 67, 67, 0.08);
	}
	&.MuiIconButton-root:hover .MuiSvgIcon-root {
		color: #f74343;
	}
`;

/**
 * The ban / unban toggle button, identical everywhere (classrooms, standalone
 * courses, bundles, Subscriptions): a compact ⊘ that is red while banned,
 * grey while not, and goes red on hover. Forwards its ref so it can sit
 * inside a Tooltip.
 */
export const BanButton = React.forwardRef<HTMLButtonElement, IconButtonProps & { banned: boolean }>(
	({ banned, ...props }, ref) => (
		<StyledBanButton ref={ref} size="small" {...props}>
			<BanIcon banned={banned} size={22} />
		</StyledBanButton>
	)
);
BanButton.displayName = 'BanButton';

export const progressColor = (status: string | null | undefined) => {
	switch (status) {
		case 'completed':
			return '#00d66b';
		case 'in progress':
			return '#2196f3';
		default:
			return '#ff3100';
	}
};
